<template>
  <div class="page-list">
    <Overlay
      width="small"
      :autoHeight="true"
      :hasStickyHeading="true"
      :link="{
        name: 'page',
        params: {
          companyId: this.companyId,
          siteId: this.siteId,
          pageId: this.pageId
        }
      }"
    >
      <Notification
        v-if="notification.active"
        :withinOverlay="true"
        :content="notification.content"
        :type="notification.type"
      />

      <PageHeader>
        <div class="heading">
          <h1>{{ list.name }}</h1>

          <Btn subtle hasIcon tooltip="Edit Section">
            <router-link
              :to="{
                name: 'edit-list',
                params: {
                  companyId: this.companyId,
                  siteId: this.siteId,
                  pageId: this.pageId,
                  listId: this.listId
                }
              }"
            >
              <Sprite name="edit" />
            </router-link>
          </Btn>
        </div>

        <div class="header-actions">
          <Btn
            :isLoading="btnAddLoading"
            primary
            @click="addListItem">
            Add Item
          </Btn>
        </div>
      </PageHeader>

      <div>
        <p v-if="!hasItem">
          A page section has been created for the widget named
          <strong class="name">"{{ list.name }}"</strong
          >, but it doesn't yet have any content. Use the <strong>'+'</strong> button above to create one.
        </p>

        <section v-if="hasItem">
          <table-data
            class="list-item-table"
            :rows="listItems"
            @reorderItems="reorderItems"
            :sortable="true"
            :headings="[
              {heading: '', key: 'image'},
              {heading: '', key: 'content'},
              {heading: '', key: 'actions'}
            ]"
            :pagination="pagination"
          >
            <router-link
              :to="{
                name: 'EditListItem',
                params: {
                  companyId: companyId,
                  siteId: siteId,
                  pageId: pageId,
                  listId: listId,
                  listItemId: listItem.id
                }
              }"
              class="list-item-thumbnail"
              v-for="(listItem, index) in listItems"
              :slot="'td-' + index + '-image'"
              :key="`list-item-image-${listItem.id}`"
            >
                <ResponsiveImage
                  v-if="!!listItem.image_versions.length"
                  :imageVersions="listItem.image_versions"
                  widthName="tiny"
                  :width="600"/>

                <div v-else class="list-type-icon">
                  <Sprite :name="`list-${listType.name}`"/>
                </div>
            </router-link>

            <div
              v-for="(listItem, index) in listItems"
              :slot="'td-' + index + '-content'"
              :key="`list-item-${listItem.id}`"
            >
              <router-link
                :to="{
                  name: 'EditListItem',
                  params: {
                    companyId: companyId,
                    siteId: siteId,
                    pageId: pageId,
                    listId: listId,
                    listItemId: listItem.id
                  }
                }"
              >
                <strong>{{ listItem.key }}</strong>
                <strong class="item-updated-at">Last updated {{updatedAt(listItem)}}</strong>
              </router-link>
            </div>

            <div
              class="select-action"
              v-for="(listItem, index) in listItems"
              :slot="'td-' + index + '-actions'"
              :key="`list-item-action-${listItem.id}`"
            >
              <select class="is-dark" @change="triggerAction(listItem, $event)">
                <option>Choose Action</option>
                <option value="editItem">Edit</option>
                <option value="duplicateListItem">Duplicate</option>
                <option value="deleteListItem">Delete</option>
              </select>
            </div>
          </table-data>

          <pagination @changePage="changePage" :pagination="pageData">
          </pagination>
        </section>
      </div>
    </Overlay>
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import notification from '@/mixins/notification'
import moment from 'moment'

import debounce from 'lodash.debounce'

import demoContent from '@/mixins/demoContent'

import {readCompanySitePageListData} from '@/lib/lists'

import {
  createCompanySitePageListListItemData,
  readCompanySitePageListListItemsData,
  deleteCompanySitePageListListItemData,
  updateCompanySitePageListListItemData
} from '@/lib/list_items'

import {
  readListTypesData,
  readListTypeData
} from '@/lib/list_types'

import Pagination from '@/components/Pagination'
import Table from '@/components/Table'
import PageHeader from '@/components/PageHeader'
import Btn from '@/components/Btn'
import Sprite from '@/components/Sprite'
import Overlay from '@/components/Overlay'
import ResponsiveImage from '@/components/ResponsiveImage'

export default {
  name: 'List',

  props: {
    companyId: {
      type: String,
      required: true
    },

    siteId: {
      type: String,
      required: true
    },

    pageId: {
      type: String,
      required: true
    },

    listId: {
      type: String,
      required: true
    }
  },

  mixins: [page, pagination, notification, demoContent],

  components: {
    Pagination,
    TableData: Table,
    PageHeader,
    Btn,
    Sprite,
    Overlay,
    ResponsiveImage
  },

  data() {
    return {
      btnAddLoading: false,
      list: {},
      listTypeName: '',
      listItems: [],
      listTypes: [],
      errors: {}
    }
  },

  created() {
    this.reorderItems = debounce(
      (items) => {
        this.updateItemWeights(items)
      },
      1000,
      {trailing: true}
    )
  },

  computed: {
    title() {
      return `${this.list.name} List`
    },

    updatedAt() {
      return (item) => {
        return moment(item.updated_at).fromNow()
      }
    },

    listType() {
      return this.listTypes.find((type) => {
        return this.list.list_type_id === type.id
      })
    },

    hasItem() {
      return !!this.listItems.length
    },

    totalItems() {
      return this.listItems.length
    },

    deleteMessage() {
      return `Are you sure you want to delete this item?`
    }
  },

  methods: {
    getData() {
      readCompanySitePageListData(
        this.companyId,
        this.siteId,
        this.pageId,
        this.listId
      ).then(({data}) => {
        this.list = data

        readListTypeData(this.list.list_type_id).then(({data}) => {
          this.listTypeName = data.name
        })
      })

      readListTypesData().then(({data}) => {
        this.listTypes = data
      })

      readCompanySitePageListListItemsData(
        this.companyId,
        this.siteId,
        this.pageId,
        this.listId,
        this.pagination
      ).then(({data, page}) => {
        this.listItems = data
        this.pageData = page
      })
    },

    addListItem(listItem) {
      // Create a list item from the demo data
      let listId = this.listId
      let listItemData
      let hasImage
      let image
      let value

      // If duplicating
      if (listItem) {
        value = listItem.value
        hasImage = listItem.image_versions.length > 0

        if (hasImage) {
          image = listItem.image_versions[0].url
        }

        listItemData = {
          key: `Copy of "${listItem.key}"`,
          value: value,
          weight: listItem.weight + 1
        }
      } else {
        value = this.demoContent[this.listTypeName].items[0].value
        hasImage = value.image_url

        if (hasImage) {
          image = value.image_url
        }

        listItemData = {
          key: 'New Item',
          value: JSON.stringify(value),
          weight: this.totalItems + 1
        }
      }

      this.btnAddLoading = true

      createCompanySitePageListListItemData(
        this.companyId,
        this.siteId,
        this.pageId,
        listId,
        listItemData
      ).then(({data}) => {
        if (hasImage) {
          let item = data
          let img = {image: image}

          // Create image for list item
          updateCompanySitePageListListItemData(
            this.companyId,
            this.siteId,
            this.pageId,
            this.listId,
            item.id,
            img
          ).then(({data}) => {
            this.$emit('updatePage')
            this.$router.push({
              name: 'EditListItem',
              params: {
                companyId: this.companyId,
                pageId: this.pageId,
                siteId: this.siteId,
                listId: listId,
                listItemId: data.id
              }
            })
          })
        } else {
          this.$emit('updatePage')
          this.$router.push({
            name: 'EditListItem',
            params: {
              companyId: this.companyId,
              pageId: this.pageId,
              siteId: this.siteId,
              listId: listId,
              listItemId: data.id
            }
          })
        }
      })
    },

    triggerAction(listItem, e) {
      let value = e.target.value
      this[value](listItem)
    },

    editItem(listItem) {
      this.$router.push({
        name: 'EditListItem',
        params: {
          companyId: this.companyId,
          siteId: this.siteId,
          pageId: this.pageId,
          listId: this.listId,
          listItemId: listItem.id
        }
      })
    },

    deleteListItem(listItem) {
      deleteCompanySitePageListListItemData(
        this.companyId,
        this.siteId,
        this.pageId,
        this.listId,
        listItem.id
      ).then(({data}) => {
        this.getData()
        this.triggerNotification('success', 'item', 'deleted')
        this.$emit('updatePage')
      })
    },

    duplicateListItem(listItem) {
      this.addListItem(listItem)
    },

    updateItemWeights(items) {
      items.forEach((item, index) => {
        item.weight = index

        updateCompanySitePageListListItemData(
          this.companyId,
          this.siteId,
          this.pageId,
          this.listId,
          item.id,
          item
        ).then(({data}) => {
          this.$emit('updatePage')
        })
      })
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.list-item-table
  table
    margin: 0 0 $gap-medium

  tr
    &:hover
      .list-type-icon
        .sprite
          transform: scale(1.2)

  td
    padding: 15px 5px!important
    cursor: default!important

    &:first-child
      display: none

      @include media($media-gt-medium)
        display: block
        width: auto
        padding: $gap-small $gap-small $gap-small 0!important

    &:nth-child(2)
      width: 100%

    .action
      padding: 0 3px

  .item-updated-at
    display: block
    margin: $gap-x-small 0
    font-family: $font-body
    font-size: 12px

  .list-item-thumbnail, .list-type-icon
    width: 85px
    height: 85px
    padding: 0
    border-radius: 5px

    img
      border-radius: 5px
      box-shadow: $box-shadow
      height: 85px
      object-fit: cover

  .list-type-icon
    background: rgba(0, 0, 0, 0.15)
    position: relative
    display: flex
    align-items: center
    justify-content: center

    .sprite
      fill: #fff!important
      width: 30px
      height: 30px
      transition: transform .3s ease-in-out

  a
    width: 100%
    display: block
    padding: 4px

  button
    margin-left: 4px

  .sprite
    @include themify($themes)
      fill: themed('color-secondary')
</style>
